import * as Yup from 'yup';

export const getUtmSourceData = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const sourceKey = urlParams.get('utm_sourcekey') || '';
  const sourceUrl = window.location.href || 'https://smzh.ch/de/';
  return { sourceKey, sourceUrl };
};

export const getHiddenFieldValue = (
  key: string,
  hiddenFields?: { key: string; value: string }[],
): string => {
  return hiddenFields?.find((field) => field.key === key)?.value || '';
};

/**
 * Format FormData with mapped Salesforce fields
 */
export const buildLeadFormData = (
  values: any,
  callbackRequestNotes: string,
  hiddenFields?: { key: string; value: string }[],
): FormData => {
  const { sourceKey: utmParameter, sourceUrl } = getUtmSourceData();

  const formData = new FormData();
  formData.append('Salutation', values.gender === 'Male' ? 'Herr' : 'Frau');
  formData.append('LastName', values.lastName);
  formData.append('FirstName', values.firstName);
  formData.append('Email', values.email);
  formData.append('MobilePhone', values.phone);
  formData.append('LeadSource', 'Website');
  formData.append('CallbackRequestNotes__c', callbackRequestNotes);

  // New Salesforce mappings
  formData.append('Street', values.strasseNummer);
  formData.append('PostalCode', values.zip);
  formData.append('City', values.city);
  formData.append('nx_Jahrgang__c', values.seasonYear);
  formData.append('nx_Berufsbezeichnung__c', values.profession);
  formData.append('UTM_Parameter__c', utmParameter);
  formData.append('Source_URL__c', sourceUrl);

  // Handle hidden fields
  hiddenFields?.forEach(({ key, value }) => {
    if (key === 'UTM_Parameter__c') {
      const url = new URL(window.location.href);
      const utmSourceKey = url.searchParams.get('utm_sourcekey');
      formData.set(key, utmSourceKey || value);
    } else {
      formData.append(key, value);
    }
  });

  return formData;
};

/**
 * Generate valid season years (1925 until currentYear - 18)
 */
export const generateSeasonYears = (): number[] => {
  const startYear = 1925;
  const currentYear = new Date().getFullYear();
  const endYear = currentYear - 18; // ✅ ensures at least 18 years old

  return Array.from(
    { length: endYear - startYear + 1 },
    (_, i) => startYear + i,
  ).reverse();
};

/**
 * Yup Validation: Phone
 */
export const getPhoneValidation = (t: (key: string) => string) =>
  Yup.string()
    .test('is-valid-phone', t('forms.validation.phone_invalid'), (value) => {
      if (!value) return false;

      const swissNumberPattern = /^(\+41|41)\d{9}$/;
      if (!swissNumberPattern.test(value)) return false;

      const swissMobilePrefixes = /^(\+417[5-9]|417[5-9])/;
      if (!swissMobilePrefixes.test(value)) return false;

      if (/(.)\1{4,}/.test(value)) return false; // Reject repeated digits

      const sequentialPattern =
        /(?:01234|12345|23456|34567|45678|56789|98765|87654|76543|65432|54321|43210)/;
      if (sequentialPattern.test(value)) return false;

      return true;
    })
    .required(t('form.validation.phone_require'));

export const getFormValidationSchema = (t: (key: string) => string) =>
  Yup.object({
    firstName: Yup.string().required(t('forms.validation.firstname_require')),
    lastName: Yup.string().required(t('forms.validation.lastname_require')),
    email: Yup.string()
      .email(t('forms.validation.email_invalid'))
      .required(t('forms.validation.email_require')),
    phone: getPhoneValidation(t),
    gender: Yup.string().required(t('forms.validation.gender_require')),
    strasseNummer: Yup.string().required(t('forms.validation.street_require')),
    zip: Yup.string()
      .matches(/^\d{4}$/, t('forms.validation.zip_invalid'))
      .required(t('forms.validation.zip_require')),
    city: Yup.string().required(t('forms.validation.city_require')),
    seasonYear: Yup.string().required(t('forms.validation.season_require')),
    profession: Yup.string().required(t('forms.validation.profession_require')),
  });
